<script setup lang="ts">
import { computed, ref } from "vue";
import type { EntryWithScores } from "@shared/types";
import Flag from "../common/Flag.vue";
import Medal from "../common/Medal.vue";
import StatusBadge from "../common/StatusBadge.vue";
import EntryBreakdown from "./EntryBreakdown.vue";

const props = defineProps<{ entries: EntryWithScores[] }>();
const sortKey = ref<"default" | "mainPoints" | "cardsTotal" | "goalsConceded" | "name">("default");

const expandedId = ref<number | null>(null);
function toggleExpand(id: number) {
  expandedId.value = expandedId.value === id ? null : id;
}

const sorted = computed(() => {
  if (sortKey.value === "default") return props.entries;
  const arr = [...props.entries];
  if (sortKey.value === "name") arr.sort((a, b) => a.name.localeCompare(b.name));
  else arr.sort((a, b) => b[sortKey.value as "mainPoints" | "cardsTotal" | "goalsConceded"] - a[sortKey.value as "mainPoints" | "cardsTotal" | "goalsConceded"]);
  return arr;
});
</script>

<template>
  <div class="sm:hidden space-y-3">
    <label class="block text-xs uppercase tracking-wider text-slate-500 font-semibold">
      Sort by
      <select v-model="sortKey" class="mt-1 block w-full rounded-md bg-white border border-slate-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:border-pot1">
        <option value="default">Default (points + tiebreaks)</option>
        <option value="mainPoints">Points</option>
        <option value="cardsTotal">Cards</option>
        <option value="goalsConceded">Conceded</option>
        <option value="name">Name</option>
      </select>
    </label>

    <article
      v-for="(e, i) in sorted"
      :key="e.id"
      class="rounded-xl ring-1 ring-slate-200 bg-white p-4 shadow-sm"
    >
      <header class="flex items-center justify-between">
        <div class="flex items-center gap-2">
          <Medal v-if="sortKey === 'default'" :rank="i + 1" />
          <span v-else class="text-slate-400 tabular text-sm">{{ i + 1 }}</span>
          <span class="font-semibold text-slate-900">{{ e.name }}</span>
        </div>
        <span class="tabular font-extrabold text-lg text-slate-900">{{ e.mainPoints }} <span class="text-xs font-medium text-slate-500">pts</span></span>
      </header>
      <ul class="mt-3 space-y-1.5 text-sm">
        <li class="flex items-center gap-2"><Flag :code="e.pot1.flag_code" :name="e.pot1.name" size="sm" /><span class="text-slate-700">{{ e.pot1.name }}</span><StatusBadge :team="e.pot1" size="xs" /></li>
        <li class="flex items-center gap-2"><Flag :code="e.pot2.flag_code" :name="e.pot2.name" size="sm" /><span class="text-slate-700">{{ e.pot2.name }}</span><StatusBadge :team="e.pot2" size="xs" /></li>
        <li class="flex items-center gap-2"><Flag :code="e.pot3.flag_code" :name="e.pot3.name" size="sm" /><span class="font-medium text-pot3">{{ e.pot3.name }}</span> <span class="text-xs text-slate-400">×2</span><StatusBadge :team="e.pot3" size="xs" /></li>
      </ul>
      <footer class="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500 tabular">
        <span>🟨 {{ e.yellows }} · 🟥 {{ e.reds }} · {{ e.goalsConceded }} conceded</span>
        <button
          type="button"
          class="font-semibold text-pot3 inline-flex items-center gap-1"
          :aria-expanded="expandedId === e.id"
          @click="toggleExpand(e.id)"
        >
          {{ expandedId === e.id ? "Hide" : "Breakdown" }}
          <span class="transition-transform" :class="expandedId === e.id && 'rotate-90'" aria-hidden="true">▶</span>
        </button>
      </footer>
      <div v-if="expandedId === e.id" class="mt-3 -mx-4 -mb-4 rounded-b-xl overflow-hidden">
        <EntryBreakdown :entry="e" />
      </div>
    </article>

    <p v-if="!sorted.length" class="text-center text-slate-400 py-6">No entries yet.</p>
  </div>
</template>
